import type { Localized } from "../../shared/i18n";

/**
 * Every string on the website — the home page and the four game pages.
 *
 * 🔴 ONE DICTIONARY, TYPED, SO A HALF-TRANSLATED PAGE DOES NOT COMPILE.
 * `Localized<MarketingCopy>` requires every language column to have the same
 * shape, so a new section is never one forgotten key away from rendering
 * `undefined` at a school buyer.
 *
 * 🔴 NOTHING HERE IS A CLAIM THE DOCS DO NOT MAKE. No customer names, no school
 * counts, no testimonials, no "trusted by N teachers", no compliance badge.
 * `privacy-compliance.md` §5 lists B2 (verifiable consent) and B5 (DPDP Consent
 * Manager) as NOT DONE, so no page carries a DPDP badge. What the pages do say is
 * what the architecture guarantees: pose is worked out on the device, nothing is
 * recorded, nothing is uploaded (I1), every prompt is spoken, one webcam, and the
 * NEP 2020 / NCF-FS 2022 mapping each level carries in `shared/curriculum`.
 *
 * ⚠️ THE TEACHER PREVIEW'S NUMBERS ARE ILLUSTRATIVE, AND THE CARD SAYS SO.
 * `sampleData` is printed on every preview — a dashboard figure with no label is
 * the one kind of number a procurement officer reasonably reads as a result.
 *
 * ⚠️ PROPER NOUNS — "Kalqy", "NEP 2020", "NCF-FS 2022", the game names — are the
 * same in every language column. No document gives another form for any of them.
 */

/** One step of a three-step explanation. */
export interface StepCopy {
  title: string;
  body: string;
}

/** A question and its answer. */
export interface QaCopy {
  q: string;
  a: string;
}

export interface MarketingCopy {
  /* ── Chrome ────────────────────────────────────────────────────────────── */
  skipToContent: string;
  logIn: string;
  openMenu: string;
  closeMenu: string;
  homeLabel: string;
  /** The `aria-label` on the `<nav>` itself — a landmark needs a name. */
  navLabel: string;
  /**
   * The header's section links. Every one of them scrolls to a section that
   * exists on the home page (see `SECTION_IDS`) — and from a game page, opens the
   * home page and then scrolls, through `requestSection`.
   */
  nav: Record<SectionKey, string>;

  /** The header's Games dropdown. */
  gamesMenu: {
    /** The panel's accessible name. */
    label: string;
    seeAll: string;
  };

  /** The calls to action, in one place so the same verb is the same words everywhere. */
  cta: {
    /** The page's one conversion. Always pink, always goes to Connect. */
    bookDemo: string;
    bookSchoolDemo: string;
    playSkyQuestFree: string;
    playFreeNow: string;
    getForSchool: string;
    watchInAction: string;
    allGames: string;
  };

  /** The background-video control (WCAG 2.2.2: motion longer than 5 s can be paused). */
  video: {
    pause: string;
    play: string;
  };

  /* ── Home: hero ────────────────────────────────────────────────────────── */
  hero: {
    eyebrow: string;
    headlineMove: string;
    headlinePlay: string;
    /** The coloured word — what a school is actually paying for. */
    headlineLearn: string;
    subline: string;
    proof: [string, string, string];
    /** The floating prompt bubble: a sample of what a child hears. */
    promptLabel: string;
    prompt: string;
    /** The camera card — illustrative, never a live feed. */
    cameraChip: string;
    cameraCaption: string;
    castAlt: string;
  };

  /* ── Home: trust strip ─────────────────────────────────────────────────── */
  /**
   * FIVE NAMED, CHECKABLE FACTS — NOT FIVE REASSURING ADJECTIVES. Each one names
   * the thing it asserts (the standard by its number, the hardware, the camera
   * behaviour), so a school can check it.
   */
  trust: [StepCopy, StepCopy, StepCopy, StepCopy, StepCopy];

  /* ── Home: how it works ────────────────────────────────────────────────── */
  how: {
    label: string;
    heading: string;
    imageAlt: string;
    chipMove: StepCopy;
    chipSlot: StepCopy;
    steps: [StepCopy, StepCopy, StepCopy];
  };

  /* ── Home: the games ───────────────────────────────────────────────────── */
  games: {
    label: string;
    heading: string;
    /** The one age range the product has — there is no per-game age data. */
    ages: string;
    freeBadge: string;
    lockedBadge: string;
    /** A card's accessible name. */
    openPage: (title: string) => string;
    lockedLabel: (title: string) => string;
  };

  /* ── Home: privacy ─────────────────────────────────────────────────────── */
  privacy: {
    label: string;
    heading: string;
    imageAlt: string;
    points: [StepCopy, StepCopy, StepCopy];
  };

  /* ── Home: who it is for ───────────────────────────────────────────────── */
  audience: {
    label: string;
    heading: string;
    schools: {
      tag: string;
      heading: string;
      points: [string, string, string, string];
      mascotAlt: string;
    };
    teachers: {
      tag: string;
      heading: string;
      body: string;
    };
    parents: {
      tag: string;
      heading: string;
      body: string;
      imageAlt: string;
    };
    preview: {
      caption: string;
      students: string;
      games: string;
      minutes: string;
      levelOf: (level: number, total: number) => string;
    };
  };

  /* ── Home: curriculum ──────────────────────────────────────────────────── */
  curriculum: {
    label: string;
    heading: string;
    body: string;
  };

  /** The skill names, keyed the way the colour tokens are. */
  skills: Record<SkillKey, string>;

  /* ── Home: FAQ ─────────────────────────────────────────────────────────── */
  faq: {
    label: string;
    heading: string;
    contactHeading: string;
    contactBody: string;
    items: QaCopy[];
  };

  /* ── Closing banner (home and game pages) ──────────────────────────────── */
  final: {
    heading: string;
    sub: string;
    childAlt: string;
  };

  /* ── Game pages ────────────────────────────────────────────────────────── */
  gamePage: {
    back: string;
    breadcrumb: string;
    ages: string;
    levels: string;
    camera: string;
    howLabel: string;
    stepN: (n: number) => string;
    grownUps: string;
    watchRound: string;
    plateAlt: (title: string) => string;
    learnLabel: string;
    learnHeading: string;
    learnLede: string;
    levelN: (n: number) => string;
    ncf: (code: string) => string;
    buildsLabel: string;
    buildsHeading: string;
    teachLabel: string;
    teachHeading: string;
    teachLede: (title: string) => string;
    teachPoints: [string, string, string];
    report: {
      caption: string;
      levelN: (n: number) => string;
      readyToMoveUp: string;
      needAHand: string;
      mainSkill: string;
    };
    moreLabel: string;
    moreHeading: string;
    finalFor: (title: string) => string;
    finalFree: string;
  };

  /* ── For schools (`#/for-schools`) ─────────────────────────────────────── */
  schoolsPage: {
    eyebrow: string;
    heading: string;
    sub: string;
    heroAlt: string;
    proof: [string, string, string];
    /** Home page "Who it's for" card → this page. */
    learnMore: string;
    getsLabel: string;
    getsHeading: string;
    gets: StepCopy[];
    rolloutLabel: string;
    rolloutHeading: string;
    rollout: [StepCopy, StepCopy, StepCopy, StepCopy];
    seesLabel: string;
    seesHeading: string;
    teacherCaption: string;
    parentCaption: string;
    parentReport: {
      child: string;
      week: string;
      lines: [string, string, string];
    };
    gamesLabel: string;
    gamesHeading: string;
    finalHeading: string;
  };

  /** "Sample data" — printed on every illustrative preview. */
  sampleData: string;

  /* ── Footer ────────────────────────────────────────────────────────────── */
  footer: {
    tagline: string;
    positioning: string;
    /** Column heading over the four game pages and Connect. */
    platform: string;
    connectPage: string;
    signIn: string;
    /** The labels the doors themselves use (`EntryScreens`), not new words. */
    doors: {
      child: string;
      parent: string;
      teacher: string;
      school: string;
    };
    contact: string;
    connect: string;
    social: {
      linkedin: string;
      instagram: string;
      youtube: string;
    };
    contactLabels: {
      email: string;
      phone: string;
      address: string;
    };
    company: string;
    country: string;
    rights: string;
    backToTop: string;
  };
}

/**
 * Where to reach Kalqy — and the only copy of it in the codebase.
 *
 * 🔴 NOT LOCALIZED. An email address, a phone number and a street do not
 * translate; two copies of a phone number are two copies free to drift apart.
 *
 * ⚠️ `phoneHref` IS STORED, NOT DERIVED — the dialable string and the readable
 * one are different strings.
 *
 * 📋 OPEN: `address` names the city only. The company moved to Bengaluru and the
 * building, street and PIN were not known when this changed; an invented street
 * is worse than none. Replace it here, and only here, when it is known.
 */
export const KALQY_CONTACT = {
  email: "info@kalqy.in",
  phone: "+91 8943000243",
  phoneHref: "tel:+918943000243",
  address: "Bengaluru, Karnataka, India",
} as const;

/** The three channels Kalqy publishes. LinkedIn first — a school checks it. */
export const KALQY_SOCIAL = {
  linkedin: "https://www.linkedin.com/company/kalqy",
  instagram: "https://www.instagram.com/kalqyglobal/",
  youtube: "https://www.youtube.com/@KalqyGlobal",
} as const;

/**
 * The skills the pages name, keyed to the `--color-skill-*` tokens.
 *
 * `patterns` is the matching token's skill as Bridge Patterns teaches it.
 */
export type SkillKey =
  | "numeracy"
  | "literacy"
  | "generalAwareness"
  | "socialAwareness"
  | "patterns"
  | "coordination"
  | "balance"
  | "bodyAwareness";

const EN: MarketingCopy = {
  skipToContent: "Skip to content",
  logIn: "Log in",
  openMenu: "Open menu",
  closeMenu: "Close menu",
  homeLabel: "Kalqy home",
  navLabel: "Sections of the Kalqy website",

  nav: {
    how: "How it works",
    games: "Games",
    schools: "For schools",
    privacy: "Privacy",
    faq: "FAQ",
  },

  gamesMenu: {
    label: "Games",
    seeAll: "See all games",
  },

  cta: {
    bookDemo: "Book a demo",
    bookSchoolDemo: "Book a school demo",
    playSkyQuestFree: "Play Sky Quest free",
    playFreeNow: "Play free now",
    getForSchool: "Get it for your school",
    watchInAction: "See how it plays",
    allGames: "All games",
  },

  video: {
    pause: "Pause background",
    play: "Play background",
  },

  hero: {
    eyebrow: "Movement-based learning for ages 3–6",
    headlineMove: "Move.",
    headlinePlay: "Play.",
    headlineLearn: "Learn.",
    subline:
      "Children learn numbers, letters and the world around them by jumping, reaching and stepping in front of one webcam.",
    proof: ["No sign-up to try", "Runs in the browser", "The camera never records"],
    promptLabel: "Spoken aloud",
    prompt: "Move to the number 3!",
    cameraChip: "I can see you!",
    cameraCaption: "Pose read on this laptop",
    castAlt:
      "Turo the turtle, two children, the Kalqy robot and a lion cub, with a rainbow parrot flying overhead",
  },

  trust: [
    { title: "NEP 2020 aligned", body: "National Education Policy" },
    { title: "NCF-FS 2022 mapped", body: "Every level, every goal" },
    { title: "Every prompt spoken", body: "No reading needed" },
    { title: "One webcam", body: "No extra hardware" },
    { title: "Never records", body: "Nothing leaves the laptop" },
  ],

  how: {
    label: "How it works",
    heading: "One laptop. One webcam. The whole class moves.",
    imageAlt:
      "A class of young children jumping with their arms up in front of a big screen while their teacher watches",
    chipMove: { title: "Great jump!", body: "Answers are chosen by moving" },
    chipSlot: { title: "Thirty-minute slot", body: "The whole class, one screen" },
    steps: [
      {
        title: "Open Kalqy on any laptop",
        body: "Connect the classroom screen or projector and point one webcam at the floor space. Nothing to install.",
      },
      {
        title: "Children move to answer",
        body: "Kids jump, reach and step to pick answers. Every prompt is spoken aloud, so nobody needs to read yet.",
      },
      {
        title: "See progress, skill by skill",
        body: "Teachers see how every child is doing, and parents get a report they can actually read.",
      },
    ],
  },

  games: {
    label: "Our games",
    heading: "Worlds to explore",
    ages: "Ages 3–6",
    freeBadge: "Free to play",
    lockedBadge: "Schools only",
    openPage: (title) => `${title} — see the game`,
    lockedLabel: (title) => `${title} — talk to us about access`,
  },

  privacy: {
    label: "Privacy by design",
    heading: "The camera sees moves. It never keeps them.",
    imageAlt: "A laptop with a webcam, behind a teal shield with a tick",
    points: [
      {
        title: "Worked out on the laptop",
        body: "Body pose is calculated on the device itself. Video never leaves the room.",
      },
      {
        title: "Nothing is recorded",
        body: "No video or photos of children are ever saved.",
      },
      {
        title: "Nothing is uploaded",
        body: "Only game results reach your school’s account — never images.",
      },
    ],
  },

  audience: {
    label: "Who it’s for",
    heading: "Built for schools. Loved at home.",
    schools: {
      tag: "For schools",
      heading: "One slot a day. Every child moving.",
      points: [
        "Track progress in real time",
        "Detailed reports and insights",
        "Easy to manage classes",
        "Aligned with learning goals",
      ],
      mascotAlt: "Turo, the Kalqy turtle",
    },
    teachers: {
      tag: "For teachers",
      heading: "See who needs a hand",
      body: "Skill-by-skill progress for the whole class, without any marking.",
    },
    parents: {
      tag: "For parents",
      heading: "Know what they learned today",
      body: "A simple report on your phone, in words that make sense.",
      imageAlt:
        "A mother smiles at her phone while her daughter jumps in front of a laptop",
    },
    preview: {
      caption: "Class overview",
      students: "Students",
      games: "Games",
      minutes: "Per week",
      levelOf: (level, total) => `Level ${String(level)} of ${String(total)}`,
    },
  },

  curriculum: {
    label: "Curriculum",
    heading: "Mapped to NEP 2020 and NCF-FS 2022",
    body: "Every level is tied to a named learning goal, so a school can check exactly what each game teaches.",
  },

  skills: {
    numeracy: "Numeracy",
    literacy: "Literacy",
    generalAwareness: "General awareness",
    socialAwareness: "Social awareness",
    patterns: "Matching & patterns",
    coordination: "Coordination",
    balance: "Balance",
    bodyAwareness: "Body awareness",
  },

  faq: {
    label: "FAQ",
    heading: "Questions schools ask",
    contactHeading: "Still curious?",
    contactBody: "Talk to a real person — write or call, and we will get back to you.",
    items: [
      {
        q: "What do we need to get started?",
        a: "A laptop with a webcam, a screen or projector the class can see, and some room to move. There is nothing to install — Kalqy runs in the browser.",
      },
      {
        q: "Is the camera safe for young children?",
        a: "Yes. Body pose is worked out on the laptop itself, nothing is recorded, and no video is ever uploaded. Only game results reach your school’s account.",
      },
      {
        q: "Which ages is Kalqy for?",
        a: "Children aged three to six. Every game starts gently and grows over three levels.",
      },
      {
        q: "Do children need to read?",
        a: "No. Every prompt is spoken aloud, and answers are chosen by moving.",
      },
      {
        q: "How long is a session?",
        a: "Kalqy is built around one thirty-minute slot with the whole class in front of one screen.",
      },
      {
        q: "Can we try it before we buy?",
        a: "Yes. Sky Quest is free to play right now with no sign-up, and we are happy to show your school the rest in a demo.",
      },
    ],
  },

  final: {
    heading: "Ready to get your class moving?",
    sub: "One laptop at the front of the room, one thirty-minute slot, the whole class.",
    childAlt: "A child pointing towards the school",
  },

  gamePage: {
    back: "All games",
    breadcrumb: "Breadcrumb",
    ages: "Ages 3–6",
    levels: "3 levels",
    camera: "Played by moving in front of the camera",
    howLabel: "How to play",
    stepN: (n) => `Step ${String(n)}`,
    grownUps: "For grown-ups",
    watchRound: "Play a round free",
    plateAlt: (title) => `${title}, as it looks on the classroom screen`,
    learnLabel: "What children learn",
    learnHeading: "Three levels, one goal at a time",
    learnLede:
      "Each level is mapped to NCF-FS 2022 competencies, so a school can check exactly what it teaches.",
    levelN: (n) => `Level ${String(n)}`,
    ncf: (code) => `NCF-FS ${code}`,
    buildsLabel: "Body and brain",
    buildsHeading: "The moves build the skills",
    teachLabel: "What teachers see",
    teachHeading: "Progress, never video",
    teachLede: (title) =>
      `Every round of ${title} turns into a level and a skill for each child — the camera image itself is never kept.`,
    teachPoints: [
      "See who is ready to move up a level",
      "Spot who needs a hand, skill by skill",
      "Share a simple report with parents",
    ],
    report: {
      caption: "Class 2B · this week",
      levelN: (n) => `Level ${String(n)}`,
      readyToMoveUp: "Ready to move up",
      needAHand: "Need a hand",
      mainSkill: "Main skill",
    },
    moreLabel: "Keep exploring",
    moreHeading: "More worlds to play",
    finalFor: (title) => `Bring ${title} to your classroom`,
    finalFree: "Loved Sky Quest? Bring every game to your class.",
  },

  schoolsPage: {
    eyebrow: "Kalqy for schools",
    heading: "Thirty minutes a day. Every child moving.",
    sub: "A whole class learns numbers, letters and the world around them by moving in front of one laptop — with progress you can see, child by child.",
    heroAlt: "A preschool class jumping in front of a big screen while their teacher watches",
    proof: ["NEP 2020 & NCF-FS 2022 mapped", "One laptop, one webcam", "The camera never records"],
    learnMore: "Kalqy for schools",
    getsLabel: "What your school gets",
    getsHeading: "Everything a class needs, nothing it has to buy",
    gets: [
      { title: "Five movement games", body: "Numbers, letters, the world around us, feelings and patterns — each over three levels." },
      { title: "Curriculum-mapped levels", body: "Every level names its NCF-FS 2022 competency, so you can check what it teaches." },
      { title: "Class and roster tools", body: "Teachers set up classes and children once, then pick a game and press play." },
      { title: "Progress for every child", body: "Levels and skills per child, so a teacher sees who is ready and who needs a hand." },
      { title: "Reports for parents", body: "A simple report on a parent’s phone, in words that make sense." },
      { title: "Privacy by design", body: "Pose is worked out on the laptop. Nothing is recorded and no video is uploaded." },
    ],
    rolloutLabel: "Getting started",
    rolloutHeading: "From a demo to a class on its feet",
    rollout: [
      { title: "Book a demo", body: "Tell us about your school and we will show you Kalqy with your own classes in mind." },
      { title: "We set up your school", body: "The Kalqy team creates your school account and your first administrator." },
      { title: "Teachers add their classes", body: "Classes and children go in once, so every round is recorded against the right child." },
      { title: "Play every day", body: "One thirty-minute slot, the whole class, one screen — and the reports fill themselves." },
    ],
    seesLabel: "What teachers and parents see",
    seesHeading: "Progress, never video",
    teacherCaption: "The class at a glance, skill by skill",
    parentCaption: "A report a parent can read in a minute",
    parentReport: {
      child: "Aarav",
      week: "This week",
      lines: ["Counted to ten in Sky Quest", "Found the letters A, B and C", "Ready for Level 2 in numbers"],
    },
    gamesLabel: "The games",
    gamesHeading: "What your children will play",
    finalHeading: "Bring Kalqy to your school",
  },

  sampleData: "Sample data",

  footer: {
    tagline: "Move · Play · Learn",
    positioning:
      "Movement-based learning software for children aged three to six, played by moving in front of a webcam.",
    platform: "Explore",
    connectPage: "Book a demo",
    signIn: "Sign in",
    doors: {
      child: "Play",
      parent: "Parent",
      teacher: "Teacher",
      school: "School admin",
    },
    contact: "Get in touch",
    connect: "Follow us",
    social: {
      linkedin: "Kalqy on LinkedIn",
      instagram: "Kalqy on Instagram",
      youtube: "Kalqy on YouTube",
    },
    contactLabels: {
      email: "Email",
      phone: "Phone",
      address: "Address",
    },
    company: "Kalqy Global Private Limited",
    country: "India",
    rights: "All rights reserved.",
    backToTop: "Back to top",
  },
};

export const MARKETING_COPY: Localized<MarketingCopy> = { en: EN };

/**
 * The anchor ids the header, the footer and the scroll spy all agree on.
 *
 * 🔴 THEY ARE NOT ROUTES, AND THE DISTINCTION IS LOAD-BEARING. `useRoute` sets
 * `window.location.hash`, so an `href="#games"` would fire `hashchange`, fail
 * `matchRoute`, and push a junk entry into the back stack for every nav click.
 * Navigation to a section is `scrollIntoView` with the default prevented — see
 * `useSectionSpy`.
 *
 * ⚠️ THE ORDER HERE IS THE ORDER ON THE PAGE, and the header draws its links in
 * it. `useSectionSpy` walks the same order to decide which one is in view.
 */
export const SECTION_IDS = {
  how: "how-it-works",
  games: "games",
  schools: "who-its-for",
  privacy: "privacy",
  faq: "faq",
} as const;

export type SectionKey = keyof typeof SECTION_IDS;
